import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Printer, Share2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { computeService } from "@/lib/calc";
import { Logo360 } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

const inputClass = "bg-brand-ivory/50 border-brand-line rounded-xl h-auto py-2.5 focus-visible:ring-2 focus-visible:ring-brand-champagne";

export default function PriceCard() {
  const { t, user, fmt } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: service, isLoading, isError } = useQuery({
    queryKey: ["service", id],
    queryFn: () => api.get(`/services/${id}`).then((r) => r.data),
    retry: (failureCount, error) => {
      const status = error?.response?.status;
      if (status === 403 || status === 404) return false;
      return failureCount < 2;
    },
  });

  const calc = useMemo(() => (service ? computeService(service, user) : null), [service, user]);
  const hasCurrent = service && service.current_price != null && service.current_price !== "" && Number(service.current_price) > 0;
  const [choice, setChoice] = useState(null);
  const [note, setNote] = useState("");

  if (isLoading) {
    return <div className="min-h-screen bg-brand-ivory flex items-center justify-center"><div className="animate-pulse"><Logo360 size={56} /></div></div>;
  }

  if (isError || !service || !calc) {
    return (
      <div className="min-h-screen bg-brand-ivory flex items-center justify-center p-6">
        <div className="max-w-md text-center bg-white rounded-2xl border border-brand-line p-8">
          <Logo360 size={56} />
          <h1 className="mt-4 font-serif text-2xl font-medium text-brand-anthracite">{t("priceCard.unavailableTitle")}</h1>
          <p className="mt-2 text-brand-taupe">{t("priceCard.unavailableDesc")}</p>
          <Button onClick={() => navigate("/services")} className="mt-6 rounded-xl bg-brand-anthracite text-brand-ivory">
            {t("priceCard.backToServices")}
          </Button>
        </div>
      </div>
    );
  }

  const effectiveChoice = choice || (hasCurrent ? "current" : "commercial");
  const priceMap = { current: hasCurrent ? Number(service.current_price) : 0, recommended: calc.finalPrice, commercial: calc.commercial.nice };
  const displayPrice = priceMap[effectiveChoice] ?? calc.commercial.nice;

  const doPrint = () => window.print();
  const doShare = async () => {
    const text = `${service.name} — ${fmt(displayPrice)}`;
    if (navigator.share) { try { await navigator.share({ title: service.name, text }); } catch (_) {} }
    else { doPrint(); }
  };

  return (
    <div className="min-h-screen bg-brand-ivory">
      {/* Controls (hidden when printing) */}
      <div className="no-print border-b border-brand-line bg-white/80 backdrop-blur-md">
        <div className="max-w-3xl mx-auto flex items-center justify-between px-4 py-3">
          <button onClick={() => navigate(`/services/${id}`)} className="inline-flex items-center gap-2 text-sm text-brand-taupe hover:text-brand-anthracite" data-testid="pricecard-back">
            <ArrowLeft className="h-4 w-4" /> {t("priceCard.back")}
          </button>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={doShare} data-testid="pricecard-share" className="rounded-xl border-brand-line text-brand-anthracite hover:bg-brand-sand h-10"><Share2 className="mr-2 h-4 w-4" />{t("priceCard.share")}</Button>
            <Button onClick={doPrint} data-testid="pricecard-print" className="rounded-xl bg-brand-anthracite text-brand-ivory h-10"><Printer className="mr-2 h-4 w-4" />{t("priceCard.print")}</Button>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-4 sm:p-8 grid lg:grid-cols-5 gap-6">
        {/* Config panel */}
        <div className="no-print lg:col-span-2 space-y-5 bg-white rounded-2xl border border-brand-line p-5">
          <div>
            <Label className="mb-2 block">{t("priceCard.priceSource")}</Label>
            <RadioGroup value={effectiveChoice} onValueChange={setChoice} className="space-y-2" data-testid="pricecard-price-source">
              {hasCurrent && (
                <label className="flex items-center gap-3 rounded-xl border border-brand-line px-3 py-2.5 cursor-pointer">
                  <RadioGroupItem value="current" data-testid="pricecard-choice-current" />
                  <span className="flex-1 text-sm">{t("priceCard.useCurrent")}</span>
                  <span className="font-semibold tabular-nums">{fmt(priceMap.current)}</span>
                </label>
              )}
              <label className="flex items-center gap-3 rounded-xl border border-brand-line px-3 py-2.5 cursor-pointer">
                <RadioGroupItem value="recommended" data-testid="pricecard-choice-recommended" />
                <span className="flex-1 text-sm">{t("priceCard.useRecommended")}</span>
                <span className="font-semibold tabular-nums">{fmt(priceMap.recommended)}</span>
              </label>
              <label className="flex items-center gap-3 rounded-xl border border-brand-line px-3 py-2.5 cursor-pointer">
                <RadioGroupItem value="commercial" data-testid="pricecard-choice-commercial" />
                <span className="flex-1 text-sm">{t("priceCard.useCommercial")}</span>
                <span className="font-semibold tabular-nums">{fmt(priceMap.commercial)}</span>
              </label>
            </RadioGroup>
          </div>
          <div className="space-y-2">
            <Label>{t("priceCard.note")}</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("priceCard.notePlaceholder")} className={inputClass} data-testid="pricecard-note" maxLength={120} />
          </div>
          <p className="text-xs text-brand-taupe">{t("priceCard.ready")}</p>
        </div>

        {/* Printable card */}
        <div className="lg:col-span-3 flex items-start justify-center">
          <div className="price-card-print w-full max-w-sm bg-white rounded-2xl border border-brand-line shadow-[0_4px_20px_rgba(184,169,154,0.1)] overflow-hidden" data-testid="pricecard-preview">
            <div className="relative bg-brand-ivory px-8 pt-10 pb-8 text-center">
              <div className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-brand-blush/40 blur-2xl" />
              <div className="relative flex flex-col items-center">
                <Logo360 size={52} />
                <div className="mt-3 font-serif text-xl font-semibold text-brand-anthracite">360 Nails</div>
                {user.studio_name ? <div className="mt-1 text-xs uppercase tracking-[0.2em] text-brand-taupe" data-testid="pricecard-studio">{user.studio_name}</div> : null}
              </div>
            </div>
            <div className="px-8 py-8 text-center">
              <div className="h-px w-12 bg-brand-champagne mx-auto mb-6" />
              <h1 className="font-serif text-3xl font-medium text-brand-anthracite leading-snug" data-testid="pricecard-service-name">{service.name}</h1>
              {service.description ? <p className="mt-3 text-sm text-brand-taupe" data-testid="pricecard-description">{service.description}</p> : null}
              <div className="mt-8">
                <div className="font-serif text-5xl font-semibold text-brand-anthracite tabular-nums" data-testid="pricecard-price">{fmt(displayPrice)}</div>
              </div>
              {note ? <p className="mt-8 text-sm italic text-brand-taupe border-t border-brand-line pt-5" data-testid="pricecard-note-preview">{note}</p> : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
